import React, { useMemo, useState } from 'react';

import {
  Button,
  Caption,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Heading,
  Icon,
  Input,
  Separator,
  Stack,
  Tag,
  Text,
  toast,
} from '@eluve/components';

import {
  type FHIRServiceRequest,
  type SpecialistEntry,
  specialists as defaultSpecialists,
  formatNextAvailable,
  generateReferralNote,
  getAddressDisplay,
  getPhoneDisplay,
  getSpecialistDisplayName,
  getSpecialtyDisplay,
} from './fhirSpecialistData';

type TagColor =
  | 'gray'
  | 'lightblue'
  | 'lightgreen'
  | 'orange'
  | 'purple'
  | 'teal'
  | 'yellow'
  | 'negative'
  | 'warning'
  | 'positive'
  | 'info'
  | 'insights';

const SPECIALTY_COLORS: Record<string, TagColor> = {
  Cardiology: 'teal',
  Dermatology: 'purple',
  Orthopedics: 'orange',
  Neurology: 'lightblue',
  Gastroenterology: 'yellow',
  Endocrinology: 'lightgreen',
  Pulmonology: 'info',
  Rheumatology: 'warning',
  Oncology: 'negative',
  Ophthalmology: 'positive',
};

const MAX_DROPDOWN_RESULTS = 5;

interface SpecialistDropdownItemProps {
  entry: SpecialistEntry;
  onClick: () => void;
}

const SpecialistDropdownItem: React.FC<SpecialistDropdownItemProps> = ({
  entry,
  onClick,
}) => {
  const name = getSpecialistDisplayName(entry);
  const specialty = getSpecialtyDisplay(entry);
  const address = getAddressDisplay(entry);
  const availability = formatNextAvailable(entry.nextAvailableSlot.start);
  const tagColor = SPECIALTY_COLORS[specialty] ?? 'gray';

  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className="w-full px-3 py-2.5 text-left transition-colors hover:bg-bg-secondary"
    >
      <Stack gap={1}>
        <Stack gap={2} direction="horizontal" wrap align="center">
          <Text inline>{name}</Text>
          <Tag label={specialty} size="xs" color={tagColor} />
        </Stack>
        <Stack gap={2} direction="horizontal" align="center" justify="between">
          <Stack gap={1.5} direction="horizontal" align="center">
            <Icon name="MapPin" size="sm" className="text-text-tertiary" />
            <Text inline size="xs" color="tertiary">
              {address}
            </Text>
          </Stack>
          <Stack gap={1.5} direction="horizontal" align="center">
            <Icon name="Calendar" size="sm" className="text-text-tertiary" />
            <Text inline size="xs" color="secondary">
              {availability}
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </button>
  );
};

interface ReferralModalProps {
  entry: SpecialistEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  physicianName?: string;
  patientName?: string;
}

const ReferralModal: React.FC<ReferralModalProps> = ({
  entry,
  open,
  onOpenChange,
  physicianName,
  patientName,
}) => {
  const [referral] = useState<FHIRServiceRequest>(() =>
    generateReferralNote(
      entry,
      'Clinical evaluation and management',
      physicianName,
      patientName,
    ),
  );
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const name = getSpecialistDisplayName(entry);
  const specialty = getSpecialtyDisplay(entry);
  const address = getAddressDisplay(entry);
  const phone = getPhoneDisplay(entry);
  const availability = formatNextAvailable(entry.nextAvailableSlot.start);

  const handleSendReferral = () => {
    onOpenChange(false);
    toast.success(`Referral sent to ${name}`);
  };

  const handlePrintPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const [{ pdf }, { ReferralLetterPDF }] = await Promise.all([
        import(/* webpackIgnore: true */ '@react-pdf/renderer'),
        import(/* webpackIgnore: true */ './ReferralLetterPDF'),
      ]);
      const pdfDocument = (
        <ReferralLetterPDF
          specialist={entry}
          referral={referral}
          generatedAt={new Date()}
        />
      );
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Refer to {name}</DialogTitle>
          <DialogDescription>{specialty}</DialogDescription>
        </DialogHeader>

        <Stack gap={4} className="py-2">
          <Stack gap={2}>
            <Caption size="2xs" color="tertiary">
              Specialist Information
            </Caption>
            <div className="rounded-lg border border-border-primary p-3">
              <Stack gap={1}>
                <Text>{name}</Text>
                <Stack gap={1.5} direction="horizontal" align="center">
                  <Icon name="MapPin" className="text-text-tertiary" />
                  <Text inline size="xs" color="tertiary">
                    {address}
                  </Text>
                </Stack>
                <Stack gap={1.5} direction="horizontal" align="center">
                  <Icon name="Phone" className="text-text-tertiary" />
                  <Text inline size="xs" color="tertiary">
                    {phone}
                  </Text>
                </Stack>
                <Stack gap={1.5} direction="horizontal" align="center">
                  <Icon name="Calendar" className="text-text-tertiary" />
                  <Text inline size="xs" color="secondary">
                    {availability}
                  </Text>
                </Stack>
              </Stack>
            </div>
          </Stack>

          <Separator className="my-2" />

          <Stack gap={2}>
            <Caption size="2xs" color="tertiary">
              Referral Letter
            </Caption>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border-primary bg-bg-secondary p-3">
              <Text size="xs" color="secondary" className="whitespace-pre-wrap">
                {referral.note}
              </Text>
            </div>
          </Stack>
        </Stack>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>

          <Button
            variant="outline"
            size="sm"
            loading={isGeneratingPdf}
            onClick={handlePrintPdf}
          >
            <Icon name="FileText" size="auto" />
            Print PDF
          </Button>

          <Button size="sm" onClick={handleSendReferral}>
            <Icon name="Send" size="auto" />
            Send Referral
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export interface ReferToSpecialistProps {
  specialists?: SpecialistEntry[];
  initialSearchQuery?: string;
  physicianName?: string;
  patientName?: string;
}

export const ReferToSpecialist: React.FC<ReferToSpecialistProps> = ({
  specialists = defaultSpecialists,
  initialSearchQuery = '',
  physicianName,
  patientName,
}) => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedSpecialist, setSelectedSpecialist] =
    useState<SpecialistEntry | null>(null);

  const filteredSpecialists = useMemo(() => {
    if (!searchQuery.trim()) return specialists;
    const query = searchQuery.toLowerCase();
    return specialists.filter((entry) => {
      const name = getSpecialistDisplayName(entry).toLowerCase();
      const specialty = getSpecialtyDisplay(entry).toLowerCase();
      return name.includes(query) || specialty.includes(query);
    });
  }, [specialists, searchQuery]);

  const dropdownResults = filteredSpecialists.slice(0, MAX_DROPDOWN_RESULTS);
  const hasMore = filteredSpecialists.length > MAX_DROPDOWN_RESULTS;

  const handleSelectSpecialist = (entry: SpecialistEntry) => {
    setSelectedSpecialist(entry);
    setShowDropdown(false);
  };

  return (
    <Stack gap={4}>
      <Heading size="xl" weight="semibold">
        Refer to Specialist
      </Heading>
      <div className="relative w-full">
        <div className="relative w-full">
          <Icon
            name="Search"
            className="absolute top-1/2 left-3 -translate-y-1/2 text-text-tertiary"
          />
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setShowDropdown(false)}
            placeholder="Search specialists by name or specialty..."
            className="w-full pl-10"
          />
        </div>

        {showDropdown && (
          <div className="absolute z-50 mt-1 max-h-[400px] w-full overflow-y-auto rounded-lg border border-border-primary bg-bg-primary shadow-lg">
            {dropdownResults.length > 0 ? (
              <Stack>
                {dropdownResults.map((entry) => (
                  <SpecialistDropdownItem
                    key={entry.practitioner.id}
                    entry={entry}
                    onClick={() => handleSelectSpecialist(entry)}
                  />
                ))}
                {hasMore && (
                  <div className="border-t border-border-primary px-3 py-2 text-center">
                    <Text size="xs" color="tertiary">
                      {filteredSpecialists.length - MAX_DROPDOWN_RESULTS} more
                      results — refine your search
                    </Text>
                  </div>
                )}
              </Stack>
            ) : (
              <div className="p-4 text-center">
                <Text size="md" color="tertiary">
                  No specialists found for "{searchQuery}"
                </Text>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedSpecialist && (
        <ReferralModal
          entry={selectedSpecialist}
          open={true}
          onOpenChange={(open) => {
            if (!open) setSelectedSpecialist(null);
          }}
          physicianName={physicianName}
          patientName={patientName}
        />
      )}
    </Stack>
  );
};
